import { FormProvider, yupResolver } from '@slal/ui/react-hook-form/core';
import { CheckboxField } from '@slal/ui/react-hook-form/fields';
import { Heading } from '@slal/ui/typography';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { MaxWidthContent, Notification } from '~/components';
import {
    APPLICATION_KEY,
    useAnalytics,
    useApplicationForm,
    useCommunicationPreferencesModal,
    useCreateApplicationMutation,
} from '~/hooks';
import { declarationValidationSchema } from '~/schemas';
import { ApplicationFormKeys, DeclarationSchema } from '~/types';
import { ApplicationButtons } from '../ApplicationButtons';
import { CommunicationPreferencesArticle, CommunicationPreferencesModal } from '../CommunicationPreferences';
import { CREATE_APPLICATION_RESPONSE } from '../Confirmation';
import { DeclarationModal } from './DeclarationModal';
import { StyledDeclarationForm, StyledDeclarationLink, StyledDeclarationsLabel, StyledField } from './styles';

const DeclarationLabel = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const closeModal = useCallback(() => setIsModalOpen(false), [setIsModalOpen]);
    const openModal = useCallback(() => setIsModalOpen(true), [setIsModalOpen]);

    return (
        <>
            <StyledDeclarationsLabel tag="span">
                I've read and saved my documents. I've read the{' '}
                <StyledDeclarationLink type="button" onClick={openModal}>
                    declarations
                </StyledDeclarationLink>
                , and I am happy to proceed, which means Standard Life will set up my new Active Money Personal Pension
                and my ReAssure pension will close.
            </StyledDeclarationsLabel>
            <DeclarationModal isOpen={isModalOpen} onClose={closeModal} />
        </>
    );
};

export const DeclarationForm = () => {
    const { pushEvent } = useAnalytics();
    const { handleSubmit, form } = useApplicationForm<DeclarationSchema>(
        {
            defaultValues: { declaration: { consent: false } },
            resolver: yupResolver(declarationValidationSchema),
        },
        ApplicationFormKeys.DECLARATION
    );

    const submitButtonLabel = 'Submit';
    const { isModalOpen, openModal, closeModal, getCommunicationPreferences } = useCommunicationPreferencesModal();

    const { createApplication, data, error, loading } = useCreateApplicationMutation();

    const onValidCallback = useCallback(() => {
        const application = sessionStorage.getItem(APPLICATION_KEY);
        createApplication({
            variables: {
                input: {
                    application,
                },
            },
        });
    }, [createApplication]);

    const navigate = useNavigate();

    useEffect(() => {
        if (!data) return;
        sessionStorage.setItem(CREATE_APPLICATION_RESPONSE, JSON.stringify(data));
        navigate('/application/confirmation');
        pushEvent({ eventLabel: `${submitButtonLabel} button click - successful` });
    }, [data, navigate, pushEvent, submitButtonLabel]);

    useEffect(() => {
        if (!error) return;
        pushEvent({
            eventLabel: `${submitButtonLabel} button click - unsuccessful - service error reported when creating the application`,
        });
    }, [error, pushEvent, submitButtonLabel]);

    return (
        <MaxWidthContent>
            <Heading level={2}>Declarations</Heading>
            <FormProvider {...form}>
                <StyledDeclarationForm onSubmit={handleSubmit(onValidCallback, submitButtonLabel)}>
                    <StyledField errorFor="declaration.consent">
                        <CheckboxField label={<DeclarationLabel />} name="declaration.consent" />
                    </StyledField>
                    <CommunicationPreferencesArticle openModal={openModal} />
                    {error && <Notification level="danger" heading="An error has occurred, please try again later." />}
                    <ApplicationButtons
                        backTo="/application/bank-details"
                        continueContent={submitButtonLabel}
                        loading={loading}
                    />
                </StyledDeclarationForm>
            </FormProvider>
            <CommunicationPreferencesModal
                isOpen={isModalOpen}
                closeModal={closeModal}
                getCommunicationPreferences={getCommunicationPreferences}
            />
        </MaxWidthContent>
    );
};
